import React, { useEffect, useMemo, useState } from "react";
import PropTypes from "prop-types";

import EventService from "service/event/EventService";
import { maskPhone } from "util/maskPhone";
import PatientService from "../../service/PatientService";
import { Sparkle } from "../controls";
import { PD_V2_EDIT, PD_V2_REFRESH } from "./events";

/**
 * Patient details — middle-column FIELD/VALUE table.
 *
 * Seven rows in the same order the dashboard dialog uses (dual entry
 * point per product ruling; the two views can never drift because
 * they share `buildProfileUpdateBody` + `updatePatientProfile`):
 *
 *   1. First name          — editable
 *   2. Last name           — editable
 *   3. Mobile number       — read-only, masked "+91 XXXXX X9876"
 *   4. Gender              — editable (Male / Female)
 *   5. Date of birth       — editable
 *   6. Communication email — editable
 *   7. Work email          — read-only, fetched separately from
 *                            api-client-config-service on mount
 *
 * Every row click emits PD_V2_EDIT with a `focusField` naming the
 * form control that should get the caret (or none for read-only
 * rows — the rail still opens but no input auto-focuses, matching
 * the product ruling that read-only clicks are consistent, not inert).
 *
 * The middle column re-fetches the patient record on PD_V2_REFRESH
 * so a successful save mirrors instantly. Work email is refetched
 * too on the same tick.
 */

// Row → focusField map. Read-only rows omit focusField so the rail
// opens without auto-focusing an input.
/* Bumped on every row click; see onRowClick. Module scope so it survives the
   section's own re-renders. */
let focusSeq = 0;

const ROWS = [
  { key: "firstName", label: "First name", focus: "firstName" },
  { key: "lastName", label: "Last name", focus: "lastName" },
  { key: "phoneNo", label: "Mobile number", readOnly: true },
  { key: "gender", label: "Gender", focus: "gender" },
  { key: "dateOfBirth", label: "Date of birth", focus: "dateOfBirth" },
  { key: "email", label: "Communication email", focus: "email" },
  { key: "workEmail", label: "Work email", readOnly: true },
];

// YYYY-MM-DD from an ISO / Date-parseable string. Used to compare a
// staged `dateOfBirth` (which is always YYYY-MM-DD from the form) to
// the current record's dateOfBirth (which may be a full ISO).
const toDateOnly = (v) => {
  if (!v) return "";
  const d = new Date(v);
  return isNaN(d.getTime()) ? "" : d.toISOString().slice(0, 10);
};

const formatDate = (v) => {
  if (!v) return "";
  const d = new Date(v);
  if (isNaN(d.getTime())) return "";
  return d.toLocaleDateString("en-GB", { day: "2-digit", month: "long", year: "numeric" });
};

const genderLabel = (g) => (g === 2 ? "Male" : g === 1 ? "Female" : "—");

const PatientDetails = ({ patient, capturedItems, onEdit: onEditProp }) => {
  // Transcript-mode staging. `capturedItems` is passed in only when
  // this section is mounted inside AiTranscriptModal / CapturedSections;
  // standalone leaves it null and every staging codepath below becomes
  // a no-op. Patient details is single-record — only ONE captured
  // update exists per transcript (uniqueKey "patient_details:main").
  //
  // Table-overlay rule (matches Addictions / Anthropometric): the table
  // shows the LAST-COMMITTED snapshot ONLY — never the uncommitted AI
  // delta. The reviewer's Update click in the rail is what moves values
  // from Current → LastCommitted (via commitCaptured) and makes them
  // visible in the table. Rejected captures are dropped from the overlay
  // so a Reject reverts the row to its DB state (per user ask "On
  // reject, ignore the last changes").
  const staged = useMemo(() => {
    if (!Array.isArray(capturedItems) || capturedItems.length === 0) return null;
    const it = capturedItems.find((c) => {
      if (!c) return false;
      if (c.deletedAt) return false;
      return c.state === "added_in_call" || c.state === "edited_in_call";
    }) || null;
    const payload = it && it.lastCommitted;
    return payload ? { current: payload, aiAuthored: it.source === "ai" } : null;
  }, [capturedItems]);
  const stagedFields = useMemo(() => {
    const set = new Set();
    if (!staged) return set;
    const cur = patient || {};
    const p = staged.current;
    if (p.firstName != null && p.firstName !== cur.firstName) set.add("firstName");
    if (p.lastName != null && p.lastName !== cur.lastName) set.add("lastName");
    if (p.gender != null && p.gender !== cur.gender) set.add("gender");
    if (p.dateOfBirth != null && String(p.dateOfBirth) !== toDateOnly(cur.dateOfBirth)) set.add("dateOfBirth");
    if (p.email != null && p.email !== cur.email) set.add("email");
    return set;
  }, [staged, patient]);
  // Work email + patient object BOTH refresh on PD_V2_REFRESH, so
  // hold each in local state. `patient` from props seeds the initial
  // render but is replaced by the fresh copy after a save.
  const [live, setLive] = useState(patient);
  useEffect(() => setLive(patient), [patient]);

  const [work, setWork] = useState({ state: "loading" });
  const loadWork = () => {
    if (!live || !live.id) {
      setWork({ state: "none" });
      return;
    }
    new PatientService().getWorkEmail(
      live.id,
      (res) => {
        setWork(
          res && res.workEmail
            ? { state: "loaded", email: String(res.workEmail), off: res.status === 2 }
            : { state: "none" }
        );
      },
      (e) => {
        const none =
          Number(e && e.code) === 404 || /no work email/i.test(String((e && e.message) || ""));
        setWork({ state: none ? "none" : "error" });
      }
    );
  };
  useEffect(loadWork, [live && live.id]);

  useEffect(() => {
    // The form emits PD_V2_REFRESH after a successful save with the
    // freshly-saved patient. The AI-transcript modal also emits this
    // (with NO payload) after Save & finish so the standalone table
    // catches whatever the reviewer approved via the transcript flow.
    // When there's no fresh payload, re-fetch the patient from the
    // API so `live` reflects the just-saved values.
    const onRefresh = (fresh) => {
      if (fresh && fresh.id) {
        setLive(fresh);
        loadWork();
        return;
      }
      const currentId = (live && live.id) || (patient && patient.id);
      if (!currentId) {
        loadWork();
        return;
      }
      new PatientService().fetchPatient(
        currentId,
        (res) => {
          const p = (res && res.data) || res;
          if (p && p.id) setLive(p);
          loadWork();
        },
        () => loadWork(),
      );
    };
    EventService.addListener(PD_V2_REFRESH, onRefresh);
    return () => EventService.removeListener(PD_V2_REFRESH, onRefresh);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Staged value overlay — transcript mode only. When a field has a
  // pending captured value, that value renders instead of the
  // committed one. The row picks up the `is-staged` class so the CSS
  // paints the peach fill + sparkle icon (see below).
  const valueOf = (key) => {
    const s = staged && staged.current;
    switch (key) {
      case "firstName":
        if (s && s.firstName != null && stagedFields.has("firstName")) return s.firstName || "—";
        return live && live.firstName ? live.firstName : "—";
      case "lastName":
        if (s && s.lastName != null && stagedFields.has("lastName")) return s.lastName || "—";
        return live && live.lastName ? live.lastName : "—";
      case "phoneNo":
        return maskPhone(live && live.countryCode, live && live.phoneno) || "—";
      case "gender":
        if (s && s.gender != null && stagedFields.has("gender")) return genderLabel(s.gender);
        return live ? genderLabel(live.gender) : "—";
      case "dateOfBirth":
        if (s && s.dateOfBirth && stagedFields.has("dateOfBirth")) return formatDate(s.dateOfBirth);
        return live && live.dateOfBirth ? formatDate(live.dateOfBirth) : "—";
      case "email":
        if (s && s.email != null && stagedFields.has("email")) return s.email || "—";
        return live && live.email ? live.email : "—";
      case "workEmail":
        if (work.state === "loading") return "Loading…";
        if (work.state === "error") return "Couldn't load";
        return work.email || "—";
      default:
        return "—";
    }
  };

  const onRowClick = (row) => {
    /* A TOKEN THAT CHANGES ON EVERY CLICK. The focus is applied by an effect
       keyed on what the row asked for, and React only re-runs an effect when
       its deps CHANGE — so clicking Gender, clicking away, and clicking Gender
       again asked for the same field and moved nothing (Nittin, 2026-09-24).
       The rail stays open between rows, so there is no remount to fall back
       on. A counter rather than Date.now(): two clicks inside one millisecond
       are rare but they are exactly the case this exists to cover. */
    focusSeq += 1;
    // Read-only rows still open the rail so every row is consistently
    // interactive; focusField is omitted so no input auto-focuses.
    // In transcript mode CapturedSections passes an `onEdit` prop and
    // owns the Editor's open state directly — the event bus route
    // stays as a fallback for standalone mounts (PatientShell's aside
    // listens for PD_V2_EDIT the same way).
    if (typeof onEditProp === "function") {
      onEditProp({ focusField: row.focus || null, focusAt: focusSeq });
      return;
    }
    EventService.emit(PD_V2_EDIT, { focusField: row.focus || null, focusAt: focusSeq });
  };

  return (
    <div className="pd-card">
      <header className="pd-head">
        <h2 className="pd-title">Patient details</h2>
      </header>

      <div className="pd-table" role="table" aria-label="Patient details">
        {/* Header row (FIELD / VALUE) intentionally dropped per
            product ruling — labels weren't earning their vertical
            space; the two-column shape is self-evident. */}
        {ROWS.map((row) => {
          const isStaged = stagedFields.has(row.key);
          const stagedByAi = isStaged && staged && staged.aiAuthored;
          const rowClass = [
            "pd-row",
            row.readOnly ? "is-readonly" : "",
            isStaged ? "is-staged" : "",
            stagedByAi ? "is-ai" : "",
          ].filter(Boolean).join(" ");
          return (
            <button
              key={row.key}
              type="button"
              className={rowClass}
              role="row"
              onClick={() => onRowClick(row)}
              aria-label={`Edit patient details, ${row.label}`}
            >
              <span className="pd-cell pd-c-field" role="cell">
                {row.label}
              </span>
              <span className="pd-cell pd-c-value" role="cell">
                {/* Sparkle rides ONLY on AI-authored staged rows —
                    matches the peach + sparkle convention every other
                    transcript-mode section uses. */}
                {stagedByAi ? (
                  <Sparkle className="pd-sparkle" aria-hidden="true" />
                ) : null}
                {valueOf(row.key)}
                {row.key === "workEmail" && work.off ? (
                  <span className="pd-tag" aria-hidden="true">Deactivated</span>
                ) : null}
              </span>
            </button>
          );
        })}
      </div>
    </div>
  );
};

PatientDetails.propTypes = {
  patient: PropTypes.object.isRequired,
  /** Transcript-mode only: staged updates for the patient_details
   *  section. Standalone leaves this null. Only one item ever exists
   *  in the array — patient_details is a single-record section. */
  capturedItems: PropTypes.arrayOf(PropTypes.object),
  /** Transcript-mode direct edit handler. When present, replaces the
   *  PD_V2_EDIT event emit on row click — CapturedSections owns the
   *  editor's open state and routes through this callback for
   *  reliability. Standalone mounts leave it undefined and the event
   *  bus path runs (PatientShell's editor listens for PD_V2_EDIT). */
  onEdit: PropTypes.func,
};

PatientDetails.defaultProps = {
  capturedItems: null,
  onEdit: null,
};

export default PatientDetails;
